import type { ComputedRef, Ref } from 'vue'

import type { Project } from '@/api'
import type { ApprovalStage, WorkflowInstance } from '@/types/workflow'
import { buildGenerationRoute } from '@/utils/workflowGeneration'
import { getHistoricalMaxStage, getLatestReviewableStage } from '@/utils/workflowPresentation'

interface UseGenerationWorkspaceLifecycleOptions {
  projectId: number
  requestedStage: ComputedRef<ApprovalStage | null>
  loading: Ref<boolean>
  project: Ref<Project | null>
  workflow: Ref<WorkflowInstance | null>
  canInspectPhase: (stage: ApprovalStage, status: string) => boolean
  stageStatus: (stage: ApprovalStage) => string
  scrollWorkspaceToTop: () => void
  routerReplace: (path: string) => Promise<unknown>
  loadProject: () => Promise<Project>
  loadWorkflow: () => Promise<WorkflowInstance>
  syncEditorFromWorkflow: (force: boolean) => void
  hydrateKnowledgeContextsFromWorkflow: () => void
  hydrateStoryboardWorkspaceState: () => void
  syncConsistencyAssetsFromStoryboard: () => void
  syncQueuedKnowledgeContexts: () => Promise<void>
  applyQueuedKnowledgeSceneImages: () => Promise<void>
  applyQueuedKnowledgeConsistencyAssetImages: () => Promise<void>
  connectWorkflowSocket: () => void
  showLoadError: (message: string) => void
}

export function useGenerationWorkspaceLifecycle(options: UseGenerationWorkspaceLifecycleOptions) {
  async function handleSwitchStage(stage: ApprovalStage) {
    if (!options.canInspectPhase(stage, options.stageStatus(stage))) {
      return
    }

    options.scrollWorkspaceToTop()
    await options.routerReplace(buildGenerationRoute(options.projectId, { stage }))
  }

  async function syncRouteToWorkflowStage(preferredStage?: ApprovalStage | null) {
    const stageOrder: ApprovalStage[] = ['proposal', 'script', 'storyboard', 'final']
    const requestedStage = options.requestedStage.value
    const historicalStage = getHistoricalMaxStage(options.workflow.value)
    const historicalIndex = stageOrder.indexOf(historicalStage)
    const requestedIndex = stageOrder.indexOf(requestedStage as ApprovalStage)

    const targetStage = preferredStage
      || ((requestedStage && requestedIndex !== -1 && requestedIndex <= historicalIndex) ? requestedStage : null)
      || options.workflow.value?.pending_approval?.stage
      || getLatestReviewableStage(options.workflow.value)

    if (!targetStage || options.requestedStage.value === targetStage) {
      return
    }

    await options.routerReplace(buildGenerationRoute(options.projectId, { stage: targetStage }))
  }

  async function handleOpenPhase(stage: ApprovalStage, status: string) {
    if (!options.canInspectPhase(stage, status)) {
      return
    }

    await handleSwitchStage(stage)
  }

  async function loadBundle(showError = true) {
    try {
      const [projectResponse, workflowResponse] = await Promise.all([
        options.loadProject(),
        options.loadWorkflow(),
      ])
      options.project.value = projectResponse
      options.workflow.value = workflowResponse
      options.syncEditorFromWorkflow(false)
    } catch {
      if (showError) {
        options.showLoadError('加载生成工作台失败')
      }
    }
  }

  async function initializeWorkspace(consumeQueuedWorkspaceAction: () => Promise<boolean>) {
    options.loading.value = true
    await loadBundle(false)
    options.hydrateKnowledgeContextsFromWorkflow()
    options.hydrateStoryboardWorkspaceState()
    options.syncConsistencyAssetsFromStoryboard()
    await options.syncQueuedKnowledgeContexts()
    await options.applyQueuedKnowledgeSceneImages()
    await options.applyQueuedKnowledgeConsistencyAssetImages()
    options.loading.value = false
    options.connectWorkflowSocket()

    const consumed = await consumeQueuedWorkspaceAction()
    if (!consumed) {
      options.syncEditorFromWorkflow(true)
    }
  }

  return {
    handleOpenPhase,
    handleSwitchStage,
    initializeWorkspace,
    loadBundle,
    syncRouteToWorkflowStage,
  }
}